import { useState } from 'react';
import { AlertTriangle, Phone, MessageSquare, X } from 'lucide-react';

const DISMISS_KEY = 'bp:crisis-banner-dismissed';

/** CrisisTopBanner — top banner shown on every authenticated screen. Includes
 *  988 Suicide & Crisis Lifeline, Crisis Text Line 741741, and 911 for immediate
 *  emergencies, plus the "not a replacement for professional therapy" disclaimer.
 *  Dismissible for the current session (returns on a fresh visit). */
export default function CrisisTopBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const close = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      role="alert"
      data-testid="crisis-top-banner"
      className="sticky top-0 z-40 w-full bg-navy text-white border-b-2 border-gold shadow-md"
    >
      <div className="max-w-[1400px] mx-auto px-4 py-2.5 lg:py-3 flex items-center gap-3 lg:gap-6">
        <AlertTriangle size={18} className="text-gold shrink-0" strokeWidth={2}/>
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
          <p className="text-[11px] sm:text-xs lg:text-sm font-medium leading-tight">
            <span className="text-gold font-semibold uppercase tracking-widest text-[10px] mr-2">In crisis?</span>
            You are not alone.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] sm:text-xs lg:text-sm">
            <a
              href="tel:911"
              data-testid="crisis-top-call-911"
              className="inline-flex items-center gap-1.5 min-h-[44px] hover:text-gold transition-smooth underline underline-offset-2"
            >
              <Phone size={12}/> Call <span className="font-semibold tabular-nums">911</span>
              <span className="hidden sm:inline text-white/60 text-[10px] ml-1">(immediate danger)</span>
            </a>
            <a
              href="tel:988"
              data-testid="crisis-top-call-988"
              className="inline-flex items-center gap-1.5 min-h-[44px] hover:text-gold transition-smooth underline underline-offset-2"
            >
              <Phone size={12}/> Call or text <span className="font-semibold tabular-nums">988</span>
            </a>
            <a
              href="sms:741741&body=HOME"
              data-testid="crisis-top-text-741"
              className="inline-flex items-center gap-1.5 min-h-[44px] hover:text-gold transition-smooth underline underline-offset-2"
            >
              <MessageSquare size={12}/> Text <span className="font-semibold">HOME</span> to <span className="tabular-nums">741741</span>
            </a>
            <span className="hidden lg:inline text-white/60 text-[11px]">
              Brotherhood+ is not a replacement for professional therapy or medical care.
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          data-testid="crisis-top-dismiss"
          aria-label="Dismiss crisis banner"
          className="shrink-0 -mr-1 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-smooth"
        >
          <X size={16} strokeWidth={2}/>
        </button>
      </div>
      <p className="lg:hidden text-[10px] text-white/60 text-center pb-2 px-4 leading-snug">
        Brotherhood+ is not a replacement for professional therapy or medical care.
      </p>
    </div>
  );
}
